import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@rydres/api-client';
import { BRAND, addDaysYmd, addMonthsYmd, describeStatus, localParts, monthGrid, startOfWeek, type StatusTone } from '@rydres/shared';
import { MonthGrid, Notice, Spinner } from '@rydres/ui';
import { riderNotMine, riderRides, type RiderRide } from '../lib/passenger.js';
import { Shell, useRiderGate } from './Passenger.js';
import { RideRow, dayOf } from './RideRow.js';

const MODES = ['month', 'week', 'day'] as const;
type Mode = (typeof MODES)[number];
const SECTION = 'px-1 text-xs font-bold uppercase tracking-wide text-muted';
const HISTORY_PAGE = 20;
const show = (ymd: string, opts: Intl.DateTimeFormatOptions) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts });

/** The first and last dates a view shows. */
function viewOf(mode: Mode, day: string): [string, string] {
  if (mode === 'day') return [day, day];
  if (mode === 'week') return [startOfWeek(day), addDaysYmd(startOfWeek(day), 6)];
  const weeks = monthGrid(day);
  return [weeks[0]![0]!, weeks.at(-1)![6]!];
}

/** rydres.com/me/booking: the rider's rides by month, week or day, and the ride history. */
export function RiderBookingPage() {
  const { p, gate } = useRiderGate('Booking', `Sign in with the mobile number you book with to see your rides with your ${BRAND.name} drivers.`);
  const today = localParts(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone).ymd;
  const [mode, setMode] = useState<Mode>('month');
  const [day, setDay] = useState(today);
  const [rides, setRides] = useState<RiderRide[] | null>(null);
  const [past, setPast] = useState<RiderRide[] | null>(null);
  const [morePast, setMorePast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const signedIn = Boolean(p.me);
  const [first, last] = viewOf(mode, day);

  const load = useCallback(() => {
    // A day wider on each side: a ride's date is its driver's, which may not be this browser's.
    riderRides({ from: `${addDaysYmd(first, -1)}T00:00:00Z`, to: `${addDaysYmd(last, 2)}T00:00:00Z`, limit: 500 }).then((r) => setRides(r.rides), () => setRides((x) => x ?? []));
  }, [first, last]);
  const loadPast = useCallback((before: string, more: boolean) => {
    riderRides({ before, limit: HISTORY_PAGE }).then(
      (r) => {
        setPast((x) => (more ? [...(x ?? []), ...r.rides] : r.rides));
        setMorePast(r.rides.length === HISTORY_PAGE);
      },
      () => setPast((x) => x ?? []),
    );
  }, []);
  useEffect(() => {
    if (!signedIn) return;
    load();
    const timer = setInterval(() => document.visibilityState === 'visible' && load(), 60_000);
    return () => clearInterval(timer);
  }, [signedIn, load]);
  useEffect(() => {
    if (signedIn) loadPast(new Date().toISOString(), false);
  }, [signedIn, loadPast]);
  if (gate) return gate;

  const onDay = (ymd: string) => (rides ?? []).filter((r) => dayOf(r) === ymd).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const marks: Record<string, StatusTone[]> = {};
  for (const r of rides ?? []) (marks[dayOf(r)] ??= []).push(describeStatus(r).tone);
  const notMine = async (r: RiderRide) => {
    setError(null);
    try {
      await riderNotMine(r.id);
      load();
      loadPast(new Date().toISOString(), false);
      void p.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  };
  const step = (by: number) => setDay(mode === 'month' ? addMonthsYmd(day, by) : addDaysYmd(day, mode === 'week' ? 7 * by : by));
  const title =
    mode === 'month'
      ? show(day, { month: 'long', year: 'numeric' })
      : mode === 'week'
        ? `${show(first, { month: 'short', day: 'numeric' })} – ${show(last, { month: 'short', day: 'numeric' })}`
        : `${day === today ? 'Today, ' : ''}${show(day, { weekday: 'short', month: 'short', day: 'numeric' })}`;
  const list = (ymd: string, empty: string) => {
    const these = onDay(ymd);
    return these.length ? these.map((r) => <RideRow key={r.id} ride={r} timeOnly onNotMine={(x) => void notMine(x)} />) : <p className="px-1 text-sm text-ink2">{empty}</p>;
  };

  return (
    <Shell>
      <h1 className="text-2xl font-black">Booking</h1>
      {error && <Notice tone="danger">{error}</Notice>}
      <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
        {MODES.map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={`flex-1 rounded-full px-3 py-1.5 capitalize ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>{m}</button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => step(-1)} aria-label={`Earlier ${mode}`} className="h-11 w-11 rounded-full bg-card2 font-bold">‹</button>
        <div className="text-center font-black">{title}</div>
        <button type="button" onClick={() => step(1)} aria-label={`Later ${mode}`} className="h-11 w-11 rounded-full bg-card2 font-bold">›</button>
      </div>
      {day !== today && <button type="button" onClick={() => setDay(today)} className="w-full rounded-xl bg-card2 py-2 text-sm font-bold">Back to today</button>}

      {rides === null ? (
        <Spinner />
      ) : mode === 'month' ? (
        <>
          <MonthGrid month={day} today={today} selected={day} marks={marks} onDay={setDay} />
          <section className="space-y-2" aria-label={show(day, { month: 'long', day: 'numeric' })}>
            <h2 className={SECTION}>{show(day, { weekday: 'long', month: 'long', day: 'numeric' })}</h2>
            {list(day, 'No rides on this day.')}
          </section>
        </>
      ) : mode === 'week' ? (
        Array.from({ length: 7 }, (_, i) => addDaysYmd(first, i)).map((ymd) => (
          <section key={ymd} className="space-y-2" aria-label={show(ymd, { month: 'long', day: 'numeric' })}>
            <h2 className={SECTION}>{show(ymd, { weekday: 'short', month: 'short', day: 'numeric' })}</h2>
            {list(ymd, 'No rides.')}
          </section>
        ))
      ) : (
        <section className="space-y-2">{list(day, 'No rides on this day.')}</section>
      )}

      <section className="space-y-2 pt-2" aria-label="Ride history">
        <h2 className={SECTION}>Ride history</h2>
        {past === null ? (
          <Spinner />
        ) : past.length === 0 ? (
          <p className="px-1 text-sm text-ink2">Rides you have taken show up here.</p>
        ) : (
          past.map((r) => <RideRow key={r.id} ride={r} onNotMine={(x) => void notMine(x)} />)
        )}
        {morePast && past && (
          <button type="button" onClick={() => loadPast(past.at(-1)!.startsAt, true)} className="w-full rounded-xl bg-card2 py-3 text-sm font-bold">Show more</button>
        )}
      </section>
    </Shell>
  );
}
